import React from 'react'
import { Card, CardHeader, CardBody, CardTitle, Row, Col } from 'reactstrap'
import { Box, Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const DAYS_MIN = 1
// Resurssipoikkeuksissa 0 on sallittu: kalenteri näkyy vain kuluvan päivän osalta
const EXCEPTION_DAYS_MIN = 0
const DAYS_MAX = 90
const DEFAULT_DAYS = 14
const daysError = (min) => `Arvon tulee olla väliltä ${min}-${DAYS_MAX}`

let keyCounter = 0
const nextKey = () => {
  keyCounter += 1
  return `row-${keyCounter}`
}

const isValidDays = (value, min) => {
  const s = String(value ?? '').trim()
  if (!/^\d+$/.test(s)) {
    return false
  }
  const n = parseInt(s, 10)
  return n >= min && n <= DAYS_MAX
}

// Validates a list of rows that all carry `days` plus an id field, flagging
// missing ids, duplicates and out of range day counts.
const validateRows = (rows, idField, missingIdError, min) =>
  rows.map((row, index) => {
    let idError = null
    if (!row[idField]) {
      idError = missingIdError
    } else if (rows.findIndex((r) => r[idField] === row[idField]) !== index) {
      idError = 'Sama valinta on jo lisätty'
    }
    return { id: idError, days: isValidDays(row.days, min) ? null : daysError(min) }
  })

const hasRowErrors = (rowErrors) => rowErrors.some((e) => e.id || e.days)

const toResourceExceptions = (exceptions) =>
  (Array.isArray(exceptions) ? exceptions : []).map((e) => ({
    resourceid: e.resourceid,
    days: parseInt(e.days, 10),
  }))

const fromResourceExceptions = (exceptions) =>
  (Array.isArray(exceptions) ? exceptions : []).map((e) => ({
    _key: nextKey(),
    resourceid: e.resourceid || '',
    days: e.days ?? '',
  }))

class Pulishing extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      saving: false,
      publicdates: DEFAULT_DAYS,
      resourceExceptions: [],
      cugdates: [],
      resources: [],
      memberships: [],
      changessaved: true,
    }
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase.getConfig('publishing').onSnapshot((doc) => {
      // Don't overwrite edits that haven't been saved yet
      if (!this.state.changessaved) {
        this.setState({ loading: false })
        return
      }
      if (doc.exists) {
        const data = doc.data()
        this.setState({
          config: data,
          loading: false,
          publicdates: data.publicdates ?? DEFAULT_DAYS,
          resourceExceptions: fromResourceExceptions(data.resourceExceptions),
          cugdates: (Array.isArray(data.cugdates) ? data.cugdates : []).map((c) => ({
            _key: nextKey(),
            cugid: c.cugid || '',
            days: c.days ?? '',
            resourceExceptions: fromResourceExceptions(c.resourceExceptions),
          })),
        })
      } else {
        this.setState({ loading: false })
      }
    })
    Promise.all([this.props.firebase.getResourses(), this.props.firebase.getMemberships()])
      .then((values) => {
        const mapDocs = (snap) =>
          snap.empty
            ? []
            : snap.docs.map((doc) => ({ ...doc.data(), id: doc.id })).filter((d) => d.state !== 'deleted')
        const resources = mapDocs(values[0]).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
        const memberships = mapDocs(values[1]).sort((a, b) =>
          (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '')
        )
        this.setState({ resources, memberships })
      })
      .catch((error) => {
        console.error(error)
      })
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  }

  validate = () => {
    const publicdates = isValidDays(this.state.publicdates, DAYS_MIN) ? null : daysError(DAYS_MIN)
    const resourceExceptions = validateRows(
      this.state.resourceExceptions,
      'resourceid',
      'Valitse resurssi',
      EXCEPTION_DAYS_MIN
    )
    const cugRows = validateRows(this.state.cugdates, 'cugid', 'Valitse jäsenyys', DAYS_MIN)
    const cugdates = this.state.cugdates.map((cug, index) => ({
      ...cugRows[index],
      resourceExceptions: validateRows(cug.resourceExceptions, 'resourceid', 'Valitse resurssi', EXCEPTION_DAYS_MIN),
    }))
    const hasErrors =
      !!publicdates ||
      hasRowErrors(resourceExceptions) ||
      hasRowErrors(cugdates) ||
      cugdates.some((c) => hasRowErrors(c.resourceExceptions))
    return { publicdates, resourceExceptions, cugdates, hasErrors }
  }

  setDirty = (patch) => {
    this.setState({ ...patch, changessaved: false })
  }

  onCustomerviewChange = (event) => {
    this.setDirty({ publicdates: event.target.value })
  }

  addResourceException = () => {
    this.setDirty({
      resourceExceptions: [...this.state.resourceExceptions, { _key: nextKey(), resourceid: '', days: '' }],
    })
  }

  updateResourceException = (index, field, value) => {
    const resourceExceptions = this.state.resourceExceptions.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    this.setDirty({ resourceExceptions })
  }

  removeResourceException = (index) => {
    this.setDirty({ resourceExceptions: this.state.resourceExceptions.filter((row, i) => i !== index) })
  }

  addCug = () => {
    this.setDirty({
      cugdates: [...this.state.cugdates, { _key: nextKey(), cugid: '', days: '', resourceExceptions: [] }],
    })
  }

  updateCug = (index, field, value) => {
    const cugdates = this.state.cugdates.map((cug, i) => (i === index ? { ...cug, [field]: value } : cug))
    this.setDirty({ cugdates })
  }

  removeCug = (index) => {
    this.setDirty({ cugdates: this.state.cugdates.filter((cug, i) => i !== index) })
  }

  addCugResourceException = (cugIndex) => {
    const cugdates = this.state.cugdates.map((cug, i) =>
      i === cugIndex
        ? { ...cug, resourceExceptions: [...cug.resourceExceptions, { _key: nextKey(), resourceid: '', days: '' }] }
        : cug
    )
    this.setDirty({ cugdates })
  }

  updateCugResourceException = (cugIndex, index, field, value) => {
    const cugdates = this.state.cugdates.map((cug, i) =>
      i === cugIndex
        ? {
            ...cug,
            resourceExceptions: cug.resourceExceptions.map((row, ri) =>
              ri === index ? { ...row, [field]: value } : row
            ),
          }
        : cug
    )
    this.setDirty({ cugdates })
  }

  removeCugResourceException = (cugIndex, index) => {
    const cugdates = this.state.cugdates.map((cug, i) =>
      i === cugIndex ? { ...cug, resourceExceptions: cug.resourceExceptions.filter((row, ri) => ri !== index) } : cug
    )
    this.setDirty({ cugdates })
  }

  onSave = async () => {
    if (this.validate().hasErrors) {
      return
    }
    this.setState({ saving: true })
    await this.props.firebase.addConfig('publishing', {
      publicdates: parseInt(this.state.publicdates, 10),
      resourceExceptions: toResourceExceptions(this.state.resourceExceptions),
      cugdates: this.state.cugdates.map((cug) => ({
        cugid: cug.cugid,
        days: parseInt(cug.days, 10),
        resourceExceptions: toResourceExceptions(cug.resourceExceptions),
      })),
    })
    this.setState({ saving: false, changessaved: true })
  }

  renderExceptionRows = (exceptions, errors, onChange, onRemove) => (
    <Stack spacing={2} sx={{ mt: exceptions.length ? 2 : 0 }}>
      {exceptions.map((row, index) => (
        <Stack key={row._key} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='flex-start'>
          <TextField
            select
            size='small'
            label='Resurssi'
            value={row.resourceid || ''}
            error={!!errors[index].id}
            helperText={errors[index].id || ''}
            onChange={(event) => onChange(index, 'resourceid', event.target.value)}
            sx={{ minWidth: 280 }}
          >
            {this.state.resources.map((resource) => (
              <MenuItem key={resource.id} value={resource.id}>
                {resource.displayName || resource.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size='small'
            type='number'
            label='Päiviä eteenpäin'
            value={row.days ?? ''}
            error={!!errors[index].days}
            helperText={errors[index].days || ''}
            onChange={(event) => onChange(index, 'days', event.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: EXCEPTION_DAYS_MIN, max: DAYS_MAX },
            }}
            sx={{ width: 200 }}
          />
          <IconButton aria-label='Poista poikkeus' onClick={() => onRemove(index)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  )

  render() {
    if (this.state.loading) {
      return <></>
    }
    const validation = this.validate()
    return (
      <>
        <Card>
          <CardHeader>
            <Row>
              <Col lg='10' md='5' sm='5'>
                <CardTitle tag='h5'>Kalenterin näkyvyys</CardTitle>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Row>
              <Col>
                <TextField
                  error={!!validation.publicdates}
                  helperText={validation.publicdates || ''}
                  id='outlined-number'
                  label='Montako päivää eteenpäin kalenteri näkyy asiakkaille'
                  type='number'
                  variant='outlined'
                  value={this.state.publicdates ?? ''}
                  onChange={this.onCustomerviewChange}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: DAYS_MIN, max: DAYS_MAX },
                  }}
                  sx={{ minWidth: 420 }}
                />
              </Col>
            </Row>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant='subtitle1'>Resurssikohtaiset poikkeukset</Typography>
              <Typography variant='body2' color='text.secondary'>
                Resurssit, joille yleinen asetus ei päde. Muille resursseille käytetään yllä olevaa arvoa. Arvo 0
                tarkoittaa, että vain kuluva päivä näkyy.
              </Typography>
              {this.renderExceptionRows(
                this.state.resourceExceptions,
                validation.resourceExceptions,
                this.updateResourceException,
                this.removeResourceException
              )}
              <Button
                size='small'
                startIcon={<AddIcon />}
                onClick={this.addResourceException}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                Lisää poikkeus
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant='subtitle1'>Jäsenyyskohtaiset asetukset</Typography>
              <Typography variant='body2' color='text.secondary'>
                Jäsenyydelle voi määrittää oman näkyvyyden sekä sille omat resurssikohtaiset poikkeukset.
              </Typography>
              <Stack spacing={2} sx={{ mt: this.state.cugdates.length ? 2 : 0 }}>
                {this.state.cugdates.map((cug, cugIndex) => (
                  <Box
                    key={cug._key}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='flex-start'>
                      <TextField
                        select
                        size='small'
                        label='Jäsenyys'
                        value={cug.cugid || ''}
                        error={!!validation.cugdates[cugIndex].id}
                        helperText={validation.cugdates[cugIndex].id || ''}
                        onChange={(event) => this.updateCug(cugIndex, 'cugid', event.target.value)}
                        sx={{ minWidth: 280 }}
                      >
                        {this.state.memberships.map((membership) => (
                          <MenuItem key={membership.id} value={membership.id}>
                            {membership.name || membership.displayName || membership.id}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size='small'
                        type='number'
                        label='Päiviä eteenpäin'
                        value={cug.days ?? ''}
                        error={!!validation.cugdates[cugIndex].days}
                        helperText={validation.cugdates[cugIndex].days || ''}
                        onChange={(event) => this.updateCug(cugIndex, 'days', event.target.value)}
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { min: DAYS_MIN, max: DAYS_MAX },
                        }}
                        sx={{ width: 200 }}
                      />
                      <IconButton aria-label='Poista jäsenyys' onClick={() => this.removeCug(cugIndex)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                    <Box sx={{ mt: 2, pl: { sm: 2 } }}>
                      <Typography variant='body2' color='text.secondary'>
                        Resurssikohtaiset poikkeukset tälle jäsenyydelle
                      </Typography>
                      {this.renderExceptionRows(
                        cug.resourceExceptions,
                        validation.cugdates[cugIndex].resourceExceptions,
                        (index, field, value) => this.updateCugResourceException(cugIndex, index, field, value),
                        (index) => this.removeCugResourceException(cugIndex, index)
                      )}
                      <Button
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => this.addCugResourceException(cugIndex)}
                        sx={{ mt: 2, textTransform: 'none' }}
                      >
                        Lisää poikkeus
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Button size='small' startIcon={<AddIcon />} onClick={this.addCug} sx={{ mt: 2, textTransform: 'none' }}>
                Lisää jäsenyys
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Stack direction='row' spacing={2} alignItems='center'>
              <Button
                variant='contained'
                color='primary'
                onClick={this.onSave}
                disabled={this.state.saving || validation.hasErrors || this.state.changessaved}
                sx={{ textTransform: 'none' }}
              >
                {this.state.saving ? 'Tallennetaan...' : 'Tallenna'}
              </Button>
              {!this.state.changessaved && !this.state.saving ? (
                <Typography variant='body2' color='text.secondary'>
                  Tallentamattomia muutoksia
                </Typography>
              ) : null}
            </Stack>
          </CardBody>
        </Card>
      </>
    )
  }
}

export default Pulishing
